import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import Input from "../../../components/FormInputs";
import CustomButton from "../../../components/AddButton";
import TitleSelect from "../../../components/Dropdown";
import PhoneInput from "../../../components/phoneinput";
import FormTitle from "../../../components/FormTitle";
import { Plus, cancelButton } from "../../../common/svg";
import CustomModal from "../../../components/Modal";
import { VendorExecutiveDetailsProps } from "../../../service/interfaces/props.interface";
import { ExecutiveDetail } from "../../../service/interfaces/ApiResponse.interface";

const ExecutiveDetails: React.FC<VendorExecutiveDetailsProps & {
  useSameContact: boolean;
  setUseSameContact: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  setExecutiveData,
  register: parentRegister,
  errors,
  control: parentControl,
  vendorPhoneNumber,
  vendorEmailId,
  parentUnregister,
  executiveDetailsProp = [],
  vendorCountryCode,
  useSameContact,
  setUseSameContact,
}) => {
    const { control, setValue } = useForm();

    // Initialize forms with a more robust data structure
    const [formData, setFormData] = useState<Array<{ id: number, data: ExecutiveDetail }>>(() => {
      if (executiveDetailsProp.length > 0) {
        return executiveDetailsProp.map((detail, index) => ({
          id: index,
          data: detail || {}
        }));
      } else {
        return [{ id: 0, data: {} }];
      }
    });

    const [nextFormId, setNextFormId] = useState(
      executiveDetailsProp.length > 0 ? executiveDetailsProp.length : 1
    );

    // Get initial checkbox state from the API response
    const initialCheckboxState = useMemo(() => {
      if (executiveDetailsProp && executiveDetailsProp.length > 0 && executiveDetailsProp[0]) {
        return executiveDetailsProp[0].checkBox === 1;
      }
      return false;
    }, [executiveDetailsProp]);

    // Use only initialCheckboxState for default, do not persist in localStorage
    const [isModalOpen, setModalOpen] = useState(false);
    const [formIndexToRemove, setFormIndexToRemove] = useState<number | null>(null);
    const [prefixes, setPrefixes] = useState<Record<number, string>>({});
    const [countryCode, setCountryCode] = useState<Record<number, string>>({});
    const [removedExecutiveIds, setRemovedExecutiveIds] = useState<string[]>([]);

    // Store original values that will be used when "Use Same Contact" is unchecked
    const [originalValues, setOriginalValues] = useState({
      phoneNumber: "",
      emailId: "",
      countryCode: ""
    });

    const [originalValuesSaved, setOriginalValuesSaved] = useState(false);

    const watchedValues = useWatch({ control: parentControl });

    // Component initialization - run once
    useEffect(() => {
      // Register the checkbox value with the parent form immediately
      setTimeout(() => {
        parentRegister(`executiveDetails[0].checkBox`).onChange({
          target: {
            name: `executiveDetails[0].checkBox`,
            value: useSameContact ? 1 : 0,
          },
        });
      }, 0);

      // If checkbox is checked on mount, apply vendor details immediately
      if (useSameContact) {
        setTimeout(() => {
          if (vendorEmailId) {
            parentRegister(`executiveDetails[0].emailId`).onChange({
              target: {
                name: `executiveDetails[0].emailId`,
                value: vendorEmailId,
              },
            });
          }

          if (vendorPhoneNumber) {
            parentRegister(`executiveDetails[0].phoneNumber`).onChange({
              target: {
                name: `executiveDetails[0].phoneNumber`,
                value: vendorPhoneNumber,
              },
            });
          }

          if (vendorCountryCode) {
            parentRegister(`executiveDetails[0].countryCode`).onChange({
              target: {
                name: `executiveDetails[0].countryCode`,
                value: vendorCountryCode,
              },
            });
          }
        }, 50);
      }
    }, [useSameContact, vendorEmailId, vendorPhoneNumber, vendorCountryCode, parentRegister]);

    // Initialize values from executiveDetailsProp
    useEffect(() => {
      if (executiveDetailsProp.length > 0) {
        // Initialize prefixes from executiveDetailsProp
        const initialPrefixes: Record<number, string> = {};

        executiveDetailsProp.forEach((detail, index) => {
          if (detail.prefix) {
            initialPrefixes[index] = detail.prefix;

            // Register the prefix value with the parent form
            setTimeout(() => {
              parentRegister(`executiveDetails[${index}].prefix`).onChange({
                target: {
                  name: `executiveDetails[${index}].prefix`,
                  value: detail.prefix,
                },
              });
            }, 0);
          }
        });

        // Update prefixes state
        setPrefixes(initialPrefixes);

        // Store original values for first executive if not already saved
        if (!originalValuesSaved) {
          const firstExec = executiveDetailsProp[0];
          if (firstExec) {
            setOriginalValues({
              phoneNumber: firstExec.phoneNumber || "",
              emailId: firstExec.emailId || "",
              countryCode: firstExec.countryCode || ""
            });
            setOriginalValuesSaved(true);
          }
        }

        // Setup country codes for all executives
        const updatedCountryCodes: Record<number, string> = {};
        executiveDetailsProp.forEach((detail, index) => {
          if (detail.countryCode) {
            updatedCountryCodes[index] = detail.countryCode;
          }
        });
        setCountryCode(updatedCountryCodes);
      }
    }, [executiveDetailsProp.length, originalValuesSaved, parentRegister]);

    // Initialize executive phone numbers from executiveDetailsProp
    useEffect(() => {
      if (executiveDetailsProp.length > 0) {
        executiveDetailsProp.forEach((detail, index) => {
          if (detail.phoneNumber) {
            // Set the phone number in the form
            setTimeout(() => {
              parentRegister(`executiveDetails[${index}].phoneNumber`).onChange({
                target: {
                  name: `executiveDetails[${index}].phoneNumber`,
                  value: detail.phoneNumber,
                },
              });
            }, 100);
          }
        });
      }
    }, [executiveDetailsProp.length, parentRegister]);

    const handlePrefixChange = (index: number, newPrefix: string) => {
      setPrefixes((prev) => ({
        ...prev,
        [index]: newPrefix,
      }));
    };

    const updateExecutiveData = useCallback(() => {
      const allData: ExecutiveDetail[] = [];

      formData.forEach((form, index) => {
        const watched = watchedValues?.executiveDetails?.[index];

        if (!watched) return;

        // Check if this executive is marked for removal
        const isRemoved = form.data?.executiveId && removedExecutiveIds.includes(form.data.executiveId);

        // Don't proceed if removed
        if (isRemoved) return;

        const merged: ExecutiveDetail = {
          firstName: watched.firstName?.trim() || "",
          lastName: watched.lastName?.trim() || "",
          phoneNumber: watched.phoneNumber?.trim() || "",
          emailId: watched.emailId?.trim() || "",
          countryCode: watched.countryCode || countryCode[index] || "",
          role: watched.role?.trim() || "",
          prefix: watched.prefix || prefixes[index] || "",
          prefixData: watched.prefixData || "",
          // Always pass 0/1 value for the first executive's checkbox
          checkBox: index === 0 ? (useSameContact ? 1 : 0) : undefined,
        };

        if (form.data.executiveId) merged.executiveId = form.data.executiveId;
        if (form.data.vendorId) merged.vendorId = form.data.vendorId;

        // Check if there's any meaningful data
        const isEmpty = !(
          merged.firstName ||
          merged.lastName ||
          merged.phoneNumber ||
          merged.emailId ||
          merged.countryCode ||
          merged.role ||
          merged.prefix
        );

        // Skip adding empty or removed entries
        if (!isEmpty) {
          allData.push(merged);
        }
      });

      setExecutiveData(allData);
    }, [
      formData,
      setExecutiveData,
      prefixes,
      countryCode,
      removedExecutiveIds,
      watchedValues?.executiveDetails,
      useSameContact,
    ]);

    useEffect(() => {
      updateExecutiveData();
    }, [updateExecutiveData]);

    const handleAddForm = () => {
      // Add new form with unique ID
      const newId = nextFormId;
      setNextFormId(prev => prev + 1);

      setFormData(prev => [...prev, { id: newId, data: {} }]);

      // Set default country code for new form
      const defaultCountryCode = "";
      const newIndex = formData.length;

      setCountryCode((prev) => ({
        ...prev,
        [newIndex]: defaultCountryCode,
      }));

      // Register the countryCode field for the new form with default value
      setTimeout(() => {
        parentRegister(`executiveDetails[${newIndex}].countryCode`).onChange({
          target: {
            name: `executiveDetails[${newIndex}].countryCode`,
            value: defaultCountryCode,
          },
        });
      }, 0);
    };

    const openModal = (index: number) => {
      setFormIndexToRemove(index);
      setModalOpen(true);
    };

    const closeModal = () => setModalOpen(false);

    const handleConfirmRemove = () => {
      if (formIndexToRemove === null) return;

      // Get the form being removed
      const formToRemove = formData[formIndexToRemove];

      // Store the executive ID before removal if exists
      if (formToRemove.data?.executiveId) {
        setRemovedExecutiveIds(prev => [...prev, formToRemove.data.executiveId!]);
      }

      // Create a copy of the form data
      const newFormData = [...formData];

      // Remove the form from our copy
      newFormData.splice(formIndexToRemove, 1);

      // Unregister all form fields for all executives
      // This is necessary because we need to re-register them with updated indices
      formData.forEach((_, index) => {
        Object.keys(formData[index].data || {}).forEach(fieldName => {
          parentUnregister?.(`executiveDetails[${index}].${fieldName}`);
        });
        // Also unregister common fields that might not be in the data object
        ['firstName', 'lastName', 'phoneNumber', 'emailId', 'countryCode', 'role', 'prefix', 'checkBox'].forEach(field => {
          parentUnregister?.(`executiveDetails[${index}].${field}`);
        });
      });

      // Update form data state
      setFormData(newFormData);

      // Re-register all fields with correct indices
      setTimeout(() => {
        newFormData.forEach((form, newIndex) => {
          // Re-register all fields for this form at its new index
          if (watchedValues?.executiveDetails?.[newIndex < formIndexToRemove ? newIndex : newIndex + 1]) {
            const sourceIndex = newIndex < formIndexToRemove ? newIndex : newIndex + 1;
            const watched = watchedValues.executiveDetails[sourceIndex];

            // Re-register each field that has a value
            Object.entries(watched || {}).forEach(([field, value]) => {
              if (value !== undefined) {
                parentRegister(`executiveDetails[${newIndex}].${field}`).onChange({
                  target: {
                    name: `executiveDetails[${newIndex}].${field}`,
                    value: value,
                  },
                });
              }
            });
          }

          // Make sure countryCode is registered
          if (countryCode[newIndex < formIndexToRemove ? newIndex : newIndex + 1]) {
            const code = countryCode[newIndex < formIndexToRemove ? newIndex : newIndex + 1];
            parentRegister(`executiveDetails[${newIndex}].countryCode`).onChange({
              target: {
                name: `executiveDetails[${newIndex}].countryCode`,
                value: code,
              },
            });
          }

          // Make sure prefix is registered
          if (prefixes[newIndex < formIndexToRemove ? newIndex : newIndex + 1]) {
            const prefix = prefixes[newIndex < formIndexToRemove ? newIndex : newIndex + 1];
            parentRegister(`executiveDetails[${newIndex}].prefix`).onChange({
              target: {
                name: `executiveDetails[${newIndex}].prefix`,
                value: prefix,
              },
            });
          }

          // Special handling for first executive's checkbox
          if (newIndex === 0) {
            parentRegister(`executiveDetails[0].checkBox`).onChange({
              target: {
                name: `executiveDetails[0].checkBox`,
                value: useSameContact ? 1 : 0,
              },
            });
          }
        });
      }, 0);

      // Update countryCode state to reflect new indices
      setCountryCode(prev => {
        const newCountryCode: Record<number, string> = {};
        Object.entries(prev).forEach(([indexStr, code]) => {
          const index = parseInt(indexStr, 10);
          if (index < formIndexToRemove) {
            // Indices before the removed form stay the same
            newCountryCode[index] = code;
          } else if (index > formIndexToRemove) {
            // Indices after the removed form shift down by 1
            newCountryCode[index - 1] = code;
          }
          // The index of the removed form is skipped
        });
        return newCountryCode;
      });

      // Update prefixes state to reflect new indices
      setPrefixes(prev => {
        const newPrefixes: Record<number, string> = {};
        Object.entries(prev).forEach(([indexStr, prefix]) => {
          const index = parseInt(indexStr, 10);
          if (index < formIndexToRemove) {
            // Indices before the removed form stay the same
            newPrefixes[index] = prefix;
          } else if (index > formIndexToRemove) {
            // Indices after the removed form shift down by 1
            newPrefixes[index - 1] = prefix;
          }
          // The index of the removed form is skipped
        });
        return newPrefixes;
      });

      // Clean up and close modal
      setFormIndexToRemove(null);
      closeModal();
    };

    // Save the original values of the first executive before applying vendor details
    const saveOriginalValues = () => {
      // Get the current values from either watched values or initial values
      const currentValues = {
        phoneNumber: watchedValues?.executiveDetails?.[0]?.phoneNumber ||
          executiveDetailsProp[0]?.phoneNumber || "",
        emailId: watchedValues?.executiveDetails?.[0]?.emailId ||
          executiveDetailsProp[0]?.emailId || "",
        countryCode: watchedValues?.executiveDetails?.[0]?.countryCode ||
          countryCode[0] ||
          executiveDetailsProp[0]?.countryCode || ""
      };

      console.log("Saving original values:", currentValues); // Debug log
      setOriginalValues(currentValues);
      setOriginalValuesSaved(true);
    };

    // Function to handle the "Use Same Contact" toggle
    const handleSameContactToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
      // Prevent default to avoid form submission/refresh
      // e.preventDefault(); // REMOVE THIS LINE, it blocks checkbox toggling
      const checked = e.target.checked;

      // Before toggling on, save the current values
      if (checked && !originalValuesSaved) {
        saveOriginalValues();
      }

      // Update the state so the checkbox can be toggled
      setUseSameContact(checked);

      // Update the form field value for checkBox
      parentRegister(`executiveDetails[0].checkBox`).onChange({
        target: {
          name: `executiveDetails[0].checkBox`,
          value: checked ? 1 : 0,
        },
      });

      // Only apply to the first form (index 0)
      if (checked) {
        // Apply vendor contact details
        if (vendorEmailId) {
          parentRegister(`executiveDetails[0].emailId`).onChange({
            target: {
              name: `executiveDetails[0].emailId`,
              value: vendorEmailId,
            },
          });
        }

        if (vendorPhoneNumber) {
          parentRegister(`executiveDetails[0].phoneNumber`).onChange({
            target: {
              name: `executiveDetails[0].phoneNumber`,
              value: vendorPhoneNumber,
            },
          });
        }

        if (vendorCountryCode) {
          parentRegister(`executiveDetails[0].countryCode`).onChange({
            target: {
              name: `executiveDetails[0].countryCode`,
              value: vendorCountryCode,
            },
          });

          // Also update the state for UI
          setCountryCode(prev => ({
            ...prev,
            0: vendorCountryCode
          }));
        }
      } else {
        // When unchecked, restore the original values
        if (originalValuesSaved) {
          console.log("Restoring original values:", originalValues); // Debug log

          parentRegister(`executiveDetails[0].emailId`).onChange({
            target: {
              name: `executiveDetails[0].emailId`,
              value: originalValues.emailId,
            },
          });

          parentRegister(`executiveDetails[0].phoneNumber`).onChange({
            target: {
              name: `executiveDetails[0].phoneNumber`,
              value: originalValues.phoneNumber,
            },
          });

          parentRegister(`executiveDetails[0].countryCode`).onChange({
            target: {
              name: `executiveDetails[0].countryCode`,
              value: originalValues.countryCode,
            },
          });

          // Also update the state for UI
          setCountryCode(prev => ({
            ...prev,
            0: originalValues.countryCode
          }));
        } else {
          // If no original values were saved, clear the fields
          console.log("No original values saved, clearing fields"); // Debug log

          parentRegister(`executiveDetails[0].emailId`).onChange({
            target: {
              name: `executiveDetails[0].emailId`,
              value: "",
            },
          });

          parentRegister(`executiveDetails[0].phoneNumber`).onChange({
            target: {
              name: `executiveDetails[0].phoneNumber`,
              value: "",
            },
          });

          parentRegister(`executiveDetails[0].countryCode`).onChange({
            target: {
              name: `executiveDetails[0].countryCode`,
              value: "",
            },
          });

          // Also update the state for UI
          setCountryCode(prev => ({
            ...prev,
            0: ""
          }));
        }
      }
    };

    // Monitor vendor details changes and update form when "Use Same Contact" is checked
    const updateClientDetails = useCallback(() => {
      // Only apply to the first form (index 0) when the checkbox is checked
      if (useSameContact && formData.length > 0) {
        // Batch updates to minimize renders
        const updates: (() => void)[] = [];

        if (vendorEmailId) {
          updates.push(() => {
            parentRegister(`executiveDetails[0].emailId`).onChange({
              target: {
                name: `executiveDetails[0].emailId`,
                value: vendorEmailId,
              },
            });
          });
        }

        if (vendorPhoneNumber) {
          updates.push(() => {
            parentRegister(`executiveDetails[0].phoneNumber`).onChange({
              target: {
                name: `executiveDetails[0].phoneNumber`,
                value: vendorPhoneNumber,
              },
            });
          });
        }

        if (vendorCountryCode) {
          updates.push(() => {
            parentRegister(`executiveDetails[0].countryCode`).onChange({
              target: {
                name: `executiveDetails[0].countryCode`,
                value: vendorCountryCode,
              },
            });

            // Also update the state for UI
            setCountryCode(prev => ({
              ...prev,
              0: vendorCountryCode
            }));
          });
        }

        // Execute all updates in a single tick to avoid multiple renders
        if (updates.length > 0) {
          setTimeout(() => {
            updates.forEach(update => update());
          }, 0);
        }
      }
    }, [vendorEmailId, vendorPhoneNumber, vendorCountryCode, useSameContact, parentRegister, formData]);

    useEffect(() => {
      updateClientDetails();
    }, [updateClientDetails]);

    // Removed custom error handling functions - using React Hook Form errors directly

    const handlePhoneInputChange = (
      index: number,
      { countryCode: newCountryCode, phoneNumber }: { countryCode: string; phoneNumber: string }
    ) => {
      // Skip updating if this is the first form and useSameContact is true
      if (index === 0 && useSameContact) {
        return;
      }

      // If this is the first form and we haven't saved original values yet, save them
      if (index === 0 && !originalValuesSaved) {
        saveOriginalValues();
      }

      // If this is the first form and we're changing the value, update the original values
      if (index === 0) {
        setOriginalValues(prev => ({
          ...prev,
          phoneNumber: phoneNumber,
          countryCode: newCountryCode
        }));
      }

      setCountryCode((prev) => ({
        ...prev,
        [index]: newCountryCode,
      }));

      // Update phone number
      parentRegister(`executiveDetails[${index}].phoneNumber`).onChange({
        target: {
          name: `executiveDetails[${index}].phoneNumber`,
          value: phoneNumber,
        },
      });

      // Update country code
      parentRegister(`executiveDetails[${index}].countryCode`).onChange({
        target: {
          name: `executiveDetails[${index}].countryCode`,
          value: newCountryCode,
        },
      });
    };

    // Get the current value of an email field
    const getEmailValue = (index: number) => {
      if (index === 0 && useSameContact && vendorEmailId) {
        return vendorEmailId;
      }

      // For non-same-contact cases or other forms
      return watchedValues?.executiveDetails?.[index]?.emailId ||
        executiveDetailsProp[index]?.emailId ||
        "";
    };

    // Get the current phone value with country code
    const getFormattedPhone = (index: number) => {
      if (index === 0 && useSameContact) {
        return `${vendorCountryCode || ""}${vendorPhoneNumber || ""}`;
      }

      // For non-same-contact cases or other forms
      const currentCountryCode = countryCode[index] ||
        watchedValues?.executiveDetails?.[index]?.countryCode ||
        executiveDetailsProp[index]?.countryCode ||
        "";

      const currentPhoneNumber = watchedValues?.executiveDetails?.[index]?.phoneNumber ||
        executiveDetailsProp[index]?.phoneNumber ||
        "";

      return `${currentCountryCode}${currentPhoneNumber}`;
    };

    // Handle email change, updating original values for first form
    const handleEmailChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
      // Skip manual changes if "Use Same Contact" is enabled for first form
      if (index === 0 && useSameContact) {
        return;
      }

      // If this is the first form, update original values
      if (index === 0) {
        setOriginalValues(prev => ({
          ...prev,
          emailId: e.target.value
        }));
      }
    };

    // Get prefix value for dropdown
    const getPrefixValue = (index: number) => {
      return prefixes[index] ||
        watchedValues?.executiveDetails?.[index]?.prefix ||
        executiveDetailsProp[index]?.prefix ||
        "";
    };

    return (
      <div className="bg-white p-4 rounded-[20px]">
        <div className="flex items-center justify-between ">
          <FormTitle title="Executive Details" />
          <div className="flex items-center">
            {/* Only show checkbox for first form */}
            {formData.length > 0 && (
              <div className="gap-1 flex items-center mr-4">
                <input
                  type="checkbox"
                  id="useSameContact"
                  name="useSameContact"
                  className="w-[20px] h-[20px]"
                  checked={useSameContact}
                  onChange={handleSameContactToggle}
                />
                <label
                  htmlFor="useSameContact"
                  className="text-[14px] font-[400] leading-[24px] font-poppins cursor-pointer"
                >
                  Use Same Contact Details
                </label>
              </div>
            )}
            <CustomButton label="" icon={Plus} onClick={handleAddForm} />
          </div>
        </div>
        <div>
          {formData.map((form, index) => (
            <div key={form.id} className="mt-4">
              <div className="flex items-center justify-end gap-4">
                <p className="text-[14px] font-[600]">
                  #{(index + 1).toString().padStart(2, "0")}
                </p>

                {/* Only show remove button if there's more than one form AND this is not index 0 */}
                {formData.length > 1 && (
                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => openModal(index)}
                      type="button"
                      className="bg-red-500 text-white p-1 rounded-full shadow-md hover:bg-red-600 transition-all"
                      aria-label="Remove"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <label className="relative top-[20px] text-[#282828] font-[600] font-poppins text-[14px]">
                Executive Name
              </label>
              <div className="flex flex-col md:flex-row gap-2">
                <div className="">
                  <TitleSelect
                    value={getPrefixValue(index)}
                    onChange={(newPrefix) => {
                      handlePrefixChange(index, newPrefix);
                      const prefixEvent = {
                        target: {
                          name: `executiveDetails[${index}].prefix`,
                          value: newPrefix,
                        },
                      };

                      parentRegister(`executiveDetails[${index}].prefix`).onChange(prefixEvent);

                      // Make sure the form knows this field has been touched
                      setValue(`executiveDetails[${index}].prefix`, newPrefix, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    register={parentRegister}
                    name={`executiveDetails[${index}].prefix`}
                    errors={errors}
                    setValue={setValue}
                    label=""
                    placeholder="Select"
                  />
                </div>
                <Input
                  name={`executiveDetails[${index}].firstName`}
                  register={parentRegister}
                  errors={errors}
                  placeholder="First Name"
                  label=""
                />
                <Input
                  name={`executiveDetails[${index}].lastName`}
                  register={parentRegister}
                  errors={errors}
                  placeholder="Last Name"
                  label="Last Name"
                />
                <Input
                  label="Role"
                  name={`executiveDetails[${index}].role`}
                  errors={errors}
                  register={parentRegister}
                  placeholder="Enter Role"
                />
                <PhoneInput
                  label="Phone Number"
                  defaultCountry="IN"
                  name={`executiveDetails[${index}].phoneNumber`}
                  type="tel"
                  errors={errors}
                  maxLength={10}
                  placeholder="Enter your phone number"
                  control={parentControl}
                  defaultValue={{
                    countryCode: countryCode[index] ||
                      form.data?.countryCode ||
                      "",
                    phoneNumber: watchedValues?.executiveDetails?.[index]?.phoneNumber ||
                      form.data?.phoneNumber ||
                      "",
                  }}
                  onChange={(values) => handlePhoneInputChange(index, values)}
                  value={getFormattedPhone(index)}
                />
                <Input
                  label="Mail ID"
                  name={`executiveDetails[${index}].emailId`}
                  required
                  errors={errors}
                  register={parentRegister}
                  placeholder="Enter Mail ID"
                />

                {/* Hidden input to store the checkbox value properly */}
                {index === 0 && (
                  <input
                    type="hidden"
                    value={useSameContact ? "1" : "0"}
                    {...parentRegister(`executiveDetails[0].checkBox`, {
                      setValueAs: v => v === "1" || v === 1 ? 1 : 0
                    })}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <CustomModal
          isOpen={isModalOpen}
          content={
            <div className="flex flex-col items-center">
              <p className="text-[18px] font-[600] text-[#4B4B4B]">
                Are you sure you want to remove this form?
              </p>
              <div className="mt-4 flex gap-4">
                <button
                  onClick={handleConfirmRemove}
                  type="button"
                  className="bg-red-500 rounded-[5px] h-[37px] w-[99px] text-white"
                >
                  Yes
                </button>
                <button
                  onClick={closeModal}
                  type="button"
                  className="bg-gray-500 rounded-[5px] h-[37px] w-[99px] text-white"
                >
                  No
                </button>
              </div>
            </div>
          }
          onClose={closeModal}
        />
      </div>
    );
  };

export default ExecutiveDetails;